import React from 'react';
import PropTypes from 'prop-types';
import {
  LuBuilding2,
  LuCalendarDays,
  LuCalendarRange,
  LuClipboardX,
  LuFlag,
  LuFlagOff,
  LuFolderOpen,
  LuGraduationCap,
  LuHash,
  LuHeartPulse,
  LuIdCard,
  LuLayers,
  LuPencil,
  LuScale,
  LuShapes,
  LuToggleLeft,
  LuToggleRight,
  LuUsers,
  LuCalendarClock,
} from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import DetailPane from '../../../components/data/DetailPane';
import { useTranslation } from '../../../i18n/LanguageContext';
import {
  displayValue,
  formatIsoDate,
  getFilterValueLabel,
  initialsOf,
  isEmptyValue,
  isFinishedFlag,
  projectStatusOf,
} from '../projectColumns';
import { ProjectFlags, StatusBadge, useDueLabel } from './ProjectCell';

const text = (value) => (isEmptyValue(value) ? '' : String(value));

/**
 * The selected project's panel: identity, every action of the old action
 * bar (card, edit, staff, visits, patients, files, screen fail, finish) and
 * key details; the footer switches Aktif/Pasif. Docked beside the table on
 * wide screens, a drawer otherwise.
 */
const ProjectDetailPane = ({
  docked,
  height,
  project,
  position,
  onPrev,
  onNext,
  onClose,
  onOpenCard,
  onEdit,
  onOpenWorkers,
  onOpenVisits,
  onOpenPatients,
  onOpenFiles,
  onOpenScreenFail,
  onToggleFinished,
  onToggleStatus,
}) => {
  const { t, locale } = useTranslation();
  const dueLabel = useDueLabel();
  const label = t('projectManagement.panel.label');

  if (!project) return <DetailPane docked={docked} height={height} open={false} label={label} onClose={onClose} />;

  const active = projectStatusOf(project) === 'Aktif';
  const finished = isFinishedFlag(project);
  const phase = text(project.projectPhase);
  const due = formatIsoDate(project.projectDueDate, locale);
  const dueHint = dueLabel(project);

  const badges = (
    <>
      <StatusBadge project={project} />
      {phase && <Badge tone="info">{getFilterValueLabel(t, 'projectPhase', phase)}</Badge>}
      <ProjectFlags project={project} />
    </>
  );

  const actions = [
    { key: 'card', label: t('projectManagement.panel.card'), icon: LuIdCard, tint: 'teal', onClick: onOpenCard },
    { key: 'edit', label: t('projectManagement.panel.edit'), icon: LuPencil, tint: 'blue', onClick: onEdit },
    { key: 'workers', label: t('projectManagement.panel.workers'), icon: LuUsers, tint: 'violet', onClick: onOpenWorkers },
    { key: 'visits', label: t('projectManagement.panel.visits'), icon: LuCalendarDays, tint: 'indigo', onClick: onOpenVisits },
    { key: 'patients', label: t('projectManagement.panel.patients'), icon: LuHeartPulse, tint: 'rose', onClick: onOpenPatients },
    { key: 'files', label: t('projectManagement.panel.files'), icon: LuFolderOpen, tint: 'amber', onClick: onOpenFiles },
    { key: 'screenFail', label: t('projectManagement.panel.screenFail'), icon: LuClipboardX, tint: 'rose', onClick: onOpenScreenFail },
    {
      key: 'finish',
      label: finished ? t('projectManagement.panel.unfinish') : t('projectManagement.panel.finish'),
      title: t('projectManagement.panel.finishHint'),
      icon: finished ? LuFlagOff : LuFlag,
      tint: 'emerald',
      onClick: onToggleFinished,
    },
  ];

  const details = [
    { key: 'protocol', icon: LuHash, label: t('projectManagement.columns.protocolNumber'), value: text(project.protocolNumber), copyable: true },
    { key: 'design', icon: LuShapes, label: t('projectManagement.columns.projectDesign'), value: text(project.projectDesign) && getFilterValueLabel(t, 'projectDesign', project.projectDesign) },
    { key: 'department', icon: LuLayers, label: t('projectManagement.columns.projectDepartment'), value: text(project.projectDepartment) },
    { key: 'start', icon: LuCalendarRange, label: t('projectManagement.columns.projectDate'), value: formatIsoDate(project.projectDate, locale) },
    {
      key: 'due',
      icon: LuCalendarClock,
      label: t('projectManagement.columns.projectDueDate'),
      value: due === '-' ? '' : `${due}${dueHint ? ` · ${dueHint}` : ''}`,
    },
    { key: 'ethics', icon: LuScale, label: t('projectManagement.columns.ethicsCommitteeNumber'), value: text(project.ethicsCommitteeNumber), copyable: true },
    { key: 'centers', icon: LuBuilding2, label: t('projectManagement.columns.projectCentersType'), value: text(project.projectCentersType) },
    { key: 'coordinator', icon: LuGraduationCap, label: t('projectManagement.columns.coordinatorAcademician'), value: text(project.coordinatorAcademician) },
  ];

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={label}
      heading={t('projectManagement.panel.heading')}
      position={position}
      contentKey={project.id}
      identity={{
        avatar: initialsOf(project.titleShort || project.customer),
        gradient: 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30',
        title: displayValue(project.titleShort),
        subtitle: text(project.customer) || undefined,
        badges,
      }}
      actions={actions}
      details={details}
      footer={{
        label: active ? t('projectManagement.panel.deactivate') : t('projectManagement.panel.activate'),
        icon: active ? LuToggleLeft : LuToggleRight,
        tone: active ? 'danger' : 'success',
        title: t('projectManagement.panel.statusHint'),
        onClick: onToggleStatus,
      }}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    />
  );
};

ProjectDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  project: PropTypes.object,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onOpenCard: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onOpenWorkers: PropTypes.func.isRequired,
  onOpenVisits: PropTypes.func.isRequired,
  onOpenPatients: PropTypes.func.isRequired,
  onOpenFiles: PropTypes.func.isRequired,
  onOpenScreenFail: PropTypes.func.isRequired,
  onToggleFinished: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
};

export default ProjectDetailPane;
